import { useTranslations } from "use-intl";
import { Play, DollarSign, Clock, ShieldCheck } from "lucide-react";
import { MiniCard } from "@/components/ui/mini-card/mini-card";
import { Skeleton } from "@/components/ui/skeleton/Skeleton";
import { useWorkflowStats } from "@/hooks/api/queries/workflows.queries";
import { formatUsd, formatDurationMs } from "@/utils/format-cost-duration";

interface WorkflowMetricTilesProps {
  workflowId: string;
}

/**
 * WorkflowMetricTiles — 4 per-workflow aggregate metric cards rendered
 * above the flow canvas in OverviewTab. Values sourced from GET /workflows/:id/stats.
 */
export function WorkflowMetricTiles({ workflowId }: WorkflowMetricTilesProps) {
  const t = useTranslations("workflows");
  const { data: statsRes, isLoading } = useWorkflowStats(workflowId);
  const stats = statsRes?.data;

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-[var(--radius-xl)] border border-[var(--border)] bg-[var(--card)] p-3"
          >
            <Skeleton className="mb-2 h-3 w-20" />
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </div>
    );
  }

  const noData = t("metrics.no_data");

  const avgCostTitle =
    stats == null || stats.avgCostUsd == null
      ? noData
      : formatUsd(stats.avgCostUsd);

  const p95DurationTitle =
    stats == null || stats.p95DurationMs == null
      ? noData
      : formatDurationMs(stats.p95DurationMs);

  const avgApprovalTitle =
    stats == null || stats.avgApprovalMs == null
      ? noData
      : formatDurationMs(stats.avgApprovalMs);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <MiniCard
        head={{
          icon: <Play size={12} aria-hidden />,
          label: t("metrics.runs_24h"),
        }}
        title={stats == null ? noData : stats.runs24h.toLocaleString()}
      />
      <MiniCard
        head={{
          icon: <DollarSign size={12} aria-hidden />,
          label: t("metrics.avg_cost"),
        }}
        title={avgCostTitle}
      />
      <MiniCard
        head={{
          icon: <Clock size={12} aria-hidden />,
          label: t("metrics.p95_duration"),
        }}
        title={p95DurationTitle}
      />
      <MiniCard
        head={{
          icon: <ShieldCheck size={12} aria-hidden />,
          label: t("metrics.avg_approval"),
        }}
        title={avgApprovalTitle}
      />
    </div>
  );
}
